import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import { useTranslation } from 'react-i18next';
import { LOGIN_NOTICE_KEY, useAuth } from '@/auth/AuthContext';
import { Brand, PoweredBy } from '@/components/BrandMark';
import { changeLanguage } from '@/i18n';
import { ApiError, tenantStore } from '@/api/client';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState(tenantStore.get());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Left by a forced password change on its way out; read once, then gone.
  const [notice] = useState(() => {
    try {
      const value = sessionStorage.getItem(LOGIN_NOTICE_KEY);
      sessionStorage.removeItem(LOGIN_NOTICE_KEY);
      return value;
    } catch {
      return null;
    }
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      tenantStore.set(workspace);
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? t('auth.invalid_credentials')
          : (err as Error).message,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Stack spacing={2} sx={{ width: 400, maxWidth: '100%' }}>
        <Card>
          <CardContent component="form" onSubmit={submit}>
            <Stack spacing={2.5}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Brand size={40} maxWidth={240} titleVariant="h5" />
                </Box>
                <IconButton
                  onClick={() => void changeLanguage(i18n.language.startsWith('ar') ? 'en' : 'ar')}
                  aria-label={t('common.language')}
                >
                  <TranslateIcon />
                </IconButton>
              </Stack>

              {notice === 'password_changed' && !error && (
                <Alert severity="success">{t('Your password was changed. Sign in with the new one.')}</Alert>
              )}
              {error && <Alert severity="error">{error}</Alert>}

              {/*
                Asked only where the address does not already say it. On
                `butchers.diwan.eg` the workspace is settled before the page
                loads; on localhost one machine signs into every tenant in turn,
                so it still has to be typed.
              */}
              {!tenantStore.fromHost && (
                <TextField
                  name="workspace"
                  label={t('Workspace code')}
                  value={workspace}
                  onChange={(event) => setWorkspace(event.target.value)}
                  helperText={t('The short code of your company workspace, e.g. butchers')}
                  autoComplete="organization"
                  required
                />
              )}
              <TextField
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                required
                autoFocus
              />
              <TextField
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <Button type="submit" variant="contained" size="large" disabled={busy}>
                {busy ? t('auth.signing_in') : t('auth.sign_in')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
        <PoweredBy />
      </Stack>
    </Box>
  );
}
